/**
 * F20 (Wave 2D) — pure mapper for `PaymentMark` → `AuditEvent`. Lives in its
 * own file so the React-only component file (`PaymentMarksHistoryPanel.tsx`)
 * stays a single-export module under `react-refresh/only-export-components`.
 */
import type { AuditEvent, AuditEventKind } from '@bravemove/ui-web';
import type { PaymentMark, PaymentMarkActorKind } from '@bravemove/types';

/**
 * Derive a sensible `AuditEventKind` per the F35 design-polish convention
 * (see Section 35 — first non-lifecycle use of `AuditLogTimeline`). Admin
 * actor rows surface as `alert` so override actions read at a glance; system
 * rows are `update` (background reconciliation); customer + venue_staff rows
 * are `create` (a positive money-state event).
 */
export function actorKindToEventKind(actorKind: PaymentMarkActorKind): AuditEventKind {
  switch (actorKind) {
    case 'admin':
      return 'alert';
    case 'system':
      return 'update';
    case 'customer':
    case 'venue_staff':
    default:
      return 'create';
  }
}

export function actorKindLabel(kind: PaymentMarkActorKind): string {
  switch (kind) {
    case 'venue_staff':
      return 'Venue staff';
    case 'customer':
      return 'Customer';
    case 'admin':
      return 'Administrator';
    case 'system':
      return 'System';
    default:
      return kind;
  }
}

function methodSuffix(method: PaymentMark['method']): string {
  return method ? ` via ${method}` : '';
}

/**
 * Map a `PaymentMark` row to the shared `AuditEvent` shape consumed by
 * `<AuditLogTimeline>`. Used by both the panel and by tests asserting the
 * mapping shape.
 *
 * The shared `AuditEvent` type uses `exactOptionalPropertyTypes`-strict
 * fields (no `undefined` allowed) — we omit `targetLabel` / `details`
 * entirely when there's nothing to surface rather than emitting `undefined`.
 */
export function paymentMarkToAuditEvent(mark: PaymentMark): AuditEvent {
  const summary = `${mark.fromStatus} → ${mark.toStatus}${methodSuffix(mark.method)}`;
  const base: AuditEvent = {
    id: mark.id,
    at: mark.createdAt,
    kind: actorKindToEventKind(mark.actorKind),
    summary,
    actor: {
      name: mark.actorDisplayName ?? actorKindLabel(mark.actorKind),
    },
  };
  const withTarget: AuditEvent = mark.reference ? { ...base, targetLabel: mark.reference } : base;
  return mark.notes ? { ...withTarget, details: mark.notes } : withTarget;
}
